import * as Yup from 'yup';
import dayjs from 'dayjs';

import { Strings } from '@app/constants';

const INDIAN_MOBILE_REGEX = /^[6-9]\d{9}$/;

const isValidTimeValue = (value: unknown) => {
  if (value == null || value === '') {
    return false;
  }
  if (dayjs.isDayjs(value)) {
    return value.isValid();
  }
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }
  return typeof value === 'string' && value.trim().length > 0;
};

const hasSearchAddressValue = (value: unknown) => {
  if (value == null || value === '') {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (typeof value === 'object' && value !== null && 'label' in value) {
    return Boolean((value as { label?: string }).label?.trim());
  }
  return false;
};

const timeField = (label: string) =>
  Yup.mixed()
    .required(Strings.validation.fieldRequired(label))
    .test('is-valid-time', Strings.validation.fieldRequired(label), isValidTimeValue);

const searchAddressField = Yup.mixed()
  .required(Strings.validation.fieldRequired(Strings.field.searchAddress))
  .test(
    'has-search-address',
    Strings.validation.fieldRequired(Strings.field.searchAddress),
    hasSearchAddressValue
  );

const coverageAreaField = Yup.string()
  .required(Strings.validation.fieldRequired(Strings.field.coverageArea))
  .matches(/^[1-9]\d*$/, ({ value }) => {
    if (value === '0') {
      return Strings.validation.notZero(Strings.field.coverageArea);
    }
    return Strings.validation.intNumber(Strings.field.coverageArea);
  });

const slotSkipField = Yup.number()
  .typeError('Only Numbers Allowed')
  .transform((value, originalValue) => (originalValue === '' ? null : value))
  .when('vendorType', {
    is: 'MP',
    then: (schema) =>
      schema
        .required(Strings.validation.fieldRequired(Strings.field.slotSkip))
        .integer('Float numbers are not allowed')
        .min(0, 'Minimum skip slot value is 0'),
    otherwise: (schema) => schema.notRequired().nullable(),
  });

// ---------------addVendorSchema-------------
export const addUserSchema = Yup.object()
  .shape({
    name: Yup.string().required(Strings.validation.fieldRequired(Strings.field.name)),
    vendorType: Yup.string().required(Strings.validation.selectRequired(Strings.field.vendorType)),
    email: Yup.string().required(Strings.validation.fieldRequired(Strings.field.email)),
    address: Yup.string().required(Strings.validation.fieldRequired(Strings.field.address)),
    city: Yup.string().required(Strings.validation.fieldRequired(Strings.field.city)),
    mobileNumber: Yup.string()
      .required(Strings.validation.fieldRequired(Strings.field.mobile))
      .matches(INDIAN_MOBILE_REGEX, 'Enter a valid 10-digit Indian mobile number')
      .test(
        'max-length',
        'Max 10 digits are valid with sim operator.',
        (value) => (value?.length ?? 0) <= 10
      ),
    searchAddress: searchAddressField,
    state: Yup.string().optional(),
    country: Yup.string().required(Strings.validation.fieldRequired(Strings.field.country)),
    latitude: Yup.string().required(Strings.validation.fieldRequired(Strings.field.latitude)),
    longitude: Yup.string().required(Strings.validation.fieldRequired(Strings.field.longitude)),
    startTime: timeField(Strings.field.startTime),
    endTime: timeField(Strings.field.endTime),
    coverageArea: coverageAreaField,
    slotSkip: slotSkipField,
  })
  .required();

export const editVendorSchema = Yup.object()
  .shape({
    name: Yup.string().required(Strings.validation.fieldRequired(Strings.field.name)),
    country: Yup.string().required(Strings.validation.fieldRequired(Strings.field.country)),
    // Address/geo/time kept optional — form still submits existing values under the hood
    vendorType: Yup.string().optional(),
    email: Yup.string().optional(),
    mobileNumber: Yup.string().optional(),
    address: Yup.string().optional(),
    city: Yup.string().optional(),
    searchAddress: Yup.mixed().optional(),
    state: Yup.string().optional(),
    latitude: Yup.string().optional(),
    longitude: Yup.string().optional(),
    startTime: Yup.mixed().optional().nullable(),
    endTime: Yup.mixed().optional().nullable(),
    coverageArea: Yup.string().optional(),
    slotSkip: Yup.number().optional().nullable(),
  })
  .required();

// ---------------addAdminSchema-------------
export const addAdminSchema = Yup.object()
  .shape({
    name: Yup.string().required(Strings.validation.fieldRequired(Strings.field.name)),
    password: Yup.string().required(Strings.validation.fieldRequired(Strings.field.password)),
    email: Yup.string().required(Strings.validation.fieldRequired(Strings.field.email)),
    mobileNumber: Yup.string()
      .matches(INDIAN_MOBILE_REGEX, 'Enter a valid 10-digit Indian mobile number')
      .notRequired(),
    image: Yup.string(),
  })
  .required();

// ---------------addMasterSchema-------------
export const addMasterSchema = Yup.object()
  .shape({
    name: Yup.string().required(Strings.validation.fieldRequired(Strings.field.name)),
    mobileNumber: Yup.string()
      .required(Strings.validation.fieldRequired(Strings.field.mobile))
      .matches(INDIAN_MOBILE_REGEX, 'Enter a valid 10-digit Indian mobile number')
      .test(
        'max-length',
        'Max 10 digits are valid with sim operator.',
        (value) => (value?.length ?? 0) <= 10
      ),
    email: Yup.string().required(Strings.validation.fieldRequired(Strings.field.email)),
    startTime: timeField(Strings.field.startTime),
    endTime: timeField(Strings.field.endTime),
    country: Yup.string().required(Strings.validation.fieldRequired(Strings.field.country)),
    city: Yup.string().required(Strings.validation.fieldRequired(Strings.field.city)),
    searchAddress: searchAddressField,
    latitude: Yup.string().required(Strings.validation.fieldRequired(Strings.field.latitude)),
    longitude: Yup.string().required(Strings.validation.fieldRequired(Strings.field.longitude)),
    coverageArea: coverageAreaField,
  })
  .required();
